import React, { useState, useEffect } from 'react';
import '../scss/TyreList.scss';
import Modal from './Modal';
import TyreDetails from './TyreDetails';
import { TyreLabel, ApprovalBadge, SEASON } from './TyreLabel';
import PromotionBadge from './PromotionBadge';
import { getRemainingContent } from '../utils/textUtils';

const TyreList = ({ tyres, loading, error, onPageChange, currentPage, totalItems, sortType, onSortChange, autoOpenModal, onModalOpened }) => {
  const [selectedTyre, setSelectedTyre] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Ajout de l'état pour les quantités de chaque pneu
  const [quantities, setQuantities] = useState({});

  // Effet pour ouvrir automatiquement la modal du premier pneu si demandé
  // L'API filtre désormais par profileSlug, donc le premier résultat est le bon pneu
  useEffect(() => {
    if (autoOpenModal && tyres && tyres.length > 0 && !loading) {
      const targetTyre = tyres[0]; // Premier résultat (filtré par profileSlug + dimensions)
      
      setSelectedTyre({
        ...targetTyre,
        quantity: quantities[targetTyre.eanCode] || 2
      });
      setIsModalOpen(true);
      
      // Notifier le parent que la modal a été ouverte
      if (onModalOpened) {
        onModalOpened();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenModal, tyres, loading]);

  if (loading) return <div className="tyres-loading">Chargement des pneus...</div>;
  if (error) return <div className="tyres-error">Erreur: {error}</div>;
  if (!tyres?.length) return <div className="tyres-empty">Aucun pneu trouvé</div>;

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Fonction pour gérer le changement de quantité
  const handleQuantityChange = (tyreId, newQuantity) => {
    let quantity = parseInt(newQuantity) || 2;
    // Assurer que la quantité est paire et >= 2
    if (quantity < 2) quantity = 2;
    if (quantity % 2 !== 0) quantity = quantity + 1; // Arrondir au nombre pair supérieur
    if (quantity > 99) quantity = 98; // Maximum pair
    
    setQuantities(prev => ({
      ...prev,
      [tyreId]: quantity
    }));
  };

  // Fonction pour incrémenter/décrémenter la quantité par 2
  const adjustQuantity = (tyreId, delta) => {
    const currentQuantity = quantities[tyreId] || 2;
    const newQuantity = currentQuantity + (delta * 2); // Incrément/décrément par 2
    handleQuantityChange(tyreId, newQuantity);
  };

  const getPageItems = (current, total, delta = 1) => {
    const range = [];
    const rangeWithDots = [];
    let last;

    range.push(1);
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      range.push(i);
    }
    if (total > 1) range.push(total);

    for (const p of range) {
      if (last) {
        if (p - last === 2) rangeWithDots.push(last + 1);
        else if (p - last > 2) rangeWithDots.push('...');
      }
      rangeWithDots.push(p);
      last = p;
    }
    return rangeWithDots;
  };

  const renderPagination = () => {
    const pages = getPageItems(currentPage, totalPages, 1);

    return (
      <div className="tyres-pagination">
        <div className="pagination-pages">
          {pages.map((p, idx) =>
            p === '...' ? (
              <span key={`dots-${idx}`} className="pagination-ellipsis">…</span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`page-btn ${p === currentPage ? 'active' : ''}`}
                aria-current={p === currentPage ? 'page' : undefined}
                disabled={p === currentPage}
              >
                {p}
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="tyre-list">
      <div className="tyres-header">
        <div className="sort-controls">
          <label htmlFor="sort-select">Trier par :</label>
          <select 
            id="sort-select" 
            value={sortType || 'default'} 
            onChange={(e) => onSortChange && onSortChange(e.target.value)}
            className="sort-select"
          >
            <option value="default">Ordre par défaut</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
          </select>
        </div>
        <span className="tyres-count">{totalItems} résultats</span>
      </div>
      
      <div className="tyres-grid">
        {tyres.map((tyre, tyreIndex) => (
          <div key={tyre.eanCode} className="tyre-card">
            <div className="tyre-image">
              <div className="left-pictos">
                <TyreLabel type="fuel" value={tyre.fuelConsumption} />
                <TyreLabel type="grip" value={tyre.wetGrip} />
                <TyreLabel type="sound" value={tyre.soundLevel + 'dB'}/>
              </div>

              <img
                src={tyre.profile.imageUrl || '/wp-content/plugins/ax_tyres_catalog/app/build/svg/tyre-placeholder.jpg'}
                alt={tyre.description}
              />
              
              <div className="right-pictos">
                <TyreLabel type={tyre.season} value={SEASON[tyre.season] || tyre.season} />
				<div className="mspmsf">
					{tyre.pmsf && tyre.msMarked && (
						<img src={'/wp-content/plugins/ax_tyres_catalog/app/public/svg/ms-pmsf.svg'} alt="pmsf" />
					)}
					{tyre.pmsf && (
						<img src={'/wp-content/plugins/ax_tyres_catalog/app/public/svg/pmsf.svg'} alt="pmsf" />
					)}
					{tyre.msMarked && (
						<img src={'/wp-content/plugins/ax_tyres_catalog/app/public/svg/ms.svg'} alt="pmsf" />
					)}
				</div>
              </div>

              {tyre.promotions && tyre.promotions.length > 0 && (
                <PromotionBadge promotion={tyre.promotions[0]} />
              )}
            </div>

            <div className="tyre-info row">
              <div className="tyre-info-left col-7">
                <div className="tyre-brand">
                  {tyre.brandLogo ? (
                    <img src={tyre.brandLogo} alt={tyre.brand} />
                  ) : (
                    <h4>{tyre.brand}</h4>
                  )}
                </div>
                <div className="tyre-description">
                  Pneu {SEASON[tyre.season]} {tyre.brand} 
                  <div>
                    {tyre.profile?.name || tyre.description}
                  </div>
                </div>
              </div>
              <div className="tyre-info-right col-5">
                <div className="tyre-price">
                  <span className="price">
                    {tyre.formattedPrice}*
                  </span>
                  {tyre.isDiscount && (
                    <span className="public-price">
                      {tyre.formattedPublicPrice}
                    </span>
                  )}
                  <span className="price-notice">
                    *Prix unitaire TTC, exclusif internet
                  </span>
                </div>
              </div>
              <div className="tyre-specs-container col-12">
                <div className="tyre-specs">
                  <span>
                    {tyre.width}/{tyre.height} R{tyre.radius} {tyre.loadIndex} {tyre.speedIndex}
                    {tyre.reinforced && (
                      <TyreLabel type="reinforced" value={tyre.reinforced} />
                    )}
                    {tyre.approvalLabel && (
                      <ApprovalBadge label={tyre.approvalLabel} />
                    )}
                    {/* Labels spéciaux */}
                    {tyre.crm && (
                      <TyreLabel type="feature" value="crm" />
                    )}
                    {tyre.continentalSeal && (
                      <TyreLabel type="feature" value="continentalSeal" />
                    )}
                    {tyre.continentalSilent && (
                      <TyreLabel type="feature" value="continentalSilent" />
                    )}
                    {tyre.ev && (
                      <TyreLabel type="feature" value="ev" />
                    )}
                    {tyre.runflatType && (
                        <TyreLabel type="feature" value={tyre.runflatType} />
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="tyre-actions">
              <div className="quantity-selector">
                <button 
                  className="quantity-btn minus"
                  onClick={() => adjustQuantity(tyre.eanCode, -1)}
                  disabled={(quantities[tyre.eanCode] || 2) <= 2}
                >
                  −
                </button>
                <input
                  type="number"
                  min="2"
                  max="98"
                  step="2"
                  value={quantities[tyre.eanCode] || 2}
                  onChange={(e) => handleQuantityChange(tyre.eanCode, e.target.value)}
                  className="quantity-input"
                />
                <button 
                  className="quantity-btn plus"
                  onClick={() => adjustQuantity(tyre.eanCode, 1)}
                  disabled={(quantities[tyre.eanCode] || 2) >= 98}
                >
                  +
                </button>
              </div>
              <button
                className="btn btn-primary quote-btn"
                onClick={() => {
                  setSelectedTyre({
                    ...tyre,
                    quantity: quantities[tyre.eanCode] || 2
                  });
                  setIsModalOpen(true);
                }}
              >
                Obtenir mon devis
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Détails du pneu"
        tyreData={selectedTyre}
        remainingDescription={getRemainingContent(selectedTyre?.profile?.description)}
      >
        {selectedTyre && <TyreDetails tyre={selectedTyre} />}
      </Modal>
      
      {renderPagination()}
    </div>
  );
};

export default TyreList; 